/**
 * Story 36.3 — "Send feedback": free text to the Amatis team, from the
 * account menu (the desktop `AvatarMenu` and the phone More page's Account
 * group). UX-DR3–DR5, rulings R5 and R8.
 *
 * - The shared `ui/dialog`: a bottom sheet below 1024px, a centred dialog at
 *   `lg`; modal, focus trapped, `#root` inert, opens on the textarea (the
 *   first tabbable control after the X). Escape, Cancel, the X and system
 *   Back close it with no write; focus then returns to `returnFocusRef` (the
 *   control that opened it — the opener is gone or hidden by then).
 * - The form lives in `SendFeedbackForm`, inside the content, which mounts
 *   only while the dialog is open, so every open starts empty.
 * - The textarea is labelled and capped at 2000 characters with a count. The
 *   three category chips are optional, single-select toggles (`aria-pressed`;
 *   clicking the pressed chip unpresses it and the category is omitted).
 * - "Attached:" names what goes with the text: the page, plus this task or
 *   this chat when one is open (`appFeedbackContext.ts`). The context is read
 *   at send time and nothing outside the R5 allow-list is sent.
 * - Errors show inline (`role="alert"`), never as a toast
 *   (`skipErrorToast`); the text and chip are kept. A 429 says to try later.
 *   Success shows "Thanks — sent" (`role="status"`) and the sheet closes on
 *   its own after ~1.5s.
 */

import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useBreakpoint } from '@/lib/hooks/useBreakpoint';
import { useMountedRef } from '@/lib/hooks/useMountedRef';
import { submitAppFeedback, type AppFeedbackCategory } from '@/lib/api/feedback';
import { buildAppFeedbackContext, describeAttachedContext } from './appFeedbackContext';

export const APP_FEEDBACK_MAX_LENGTH = 2000;
/** How long "Thanks — sent" shows before the sheet closes on its own. */
export const APP_FEEDBACK_CLOSE_DELAY_MS = 1500;

const SUBNOTE = 'Goes to the Amatis team, not the agent.';
const SEND_ERROR = "Couldn't send your feedback. Please try again.";
const RATE_LIMITED_ERROR = "You've sent a lot of feedback in the last hour. Please try again later.";

const CATEGORIES: ReadonlyArray<{ value: AppFeedbackCategory; label: string }> = [
  { value: 'problem', label: 'Problem' },
  { value: 'idea', label: 'Idea' },
  { value: 'other', label: 'Other' },
];

interface SendFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The control that opened the sheet; focus returns to it on close. */
  returnFocusRef: RefObject<HTMLElement | null>;
}

export default function SendFeedbackDialog({ open, onOpenChange, returnFocusRef }: SendFeedbackDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="send-feedback-dialog"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          returnFocusRef.current?.focus();
        }}
      >
        <SendFeedbackForm onDone={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

function SendFeedbackForm({ onDone }: { onDone: () => void }) {
  const location = useLocation();
  const { isDesktop } = useBreakpoint();
  const textId = useId();
  const countId = useId();
  const chipsLabelId = useId();

  const [text, setText] = useState('');
  const [category, setCategory] = useState<AppFeedbackCategory | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A send still in flight when the sheet is dismissed must not touch state
  // or schedule a close for a sheet the user may have reopened.
  const mountedRef = useMountedRef();

  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    [],
  );

  const attached = describeAttachedContext(
    buildAppFeedbackContext({ pathname: location.pathname, search: location.search }, isDesktop),
  );
  const trimmed = text.trim();
  const canSend = trimmed.length > 0 && trimmed.length <= APP_FEEDBACK_MAX_LENGTH && !isSubmitting && !sent;
  const locked = isSubmitting || sent;

  const handleSend = async () => {
    if (!canSend) return;
    setError(null);
    setIsSubmitting(true);
    try {
      await submitAppFeedback({
        feedback_text: trimmed,
        ...(category ? { category } : {}),
        context: buildAppFeedbackContext({ pathname: location.pathname, search: location.search }, isDesktop),
      });
      if (!mountedRef.current) return;
      setSent(true);
      closeTimer.current = setTimeout(onDone, APP_FEEDBACK_CLOSE_DELAY_MS);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(axios.isAxiosError(err) && err.response?.status === 429 ? RATE_LIMITED_ERROR : SEND_ERROR);
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void handleSend();
      }}
    >
      <DialogHeader>
        <DialogTitle>Send feedback</DialogTitle>
        <DialogDescription>{SUBNOTE}</DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-2">
        <label htmlFor={textId} className="type-label text-ink">
          Your feedback
        </label>
        <textarea
          id={textId}
          value={text}
          onChange={(e) => setText(e.target.value)}
          readOnly={locked}
          maxLength={APP_FEEDBACK_MAX_LENGTH}
          aria-describedby={countId}
          rows={3}
          placeholder="A problem you hit, or an idea"
          className={cn(
            'w-full resize-none rounded-lg border p-3 text-base text-ink focus-ring lg:min-h-36',
            'border-line-strong bg-surface placeholder:text-ink-muted',
          )}
        />
        <p id={countId} className="self-end text-xs text-ink-muted">
          {text.length}/{APP_FEEDBACK_MAX_LENGTH}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <p id={chipsLabelId} className="type-label text-ink">
          Category <span className="text-ink-muted">(optional)</span>
        </p>
        <div role="group" aria-labelledby={chipsLabelId} className="flex flex-wrap gap-2">
          {CATEGORIES.map(({ value, label }) => {
            const pressed = category === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={pressed}
                disabled={locked}
                onClick={() => setCategory(pressed ? null : value)}
                className={cn(
                  'inline-flex min-h-11 min-w-11 items-center justify-center rounded-full border px-4',
                  'text-sm font-medium transition-colors motion-reduce:transition-none focus-ring',
                  'disabled:cursor-not-allowed disabled:opacity-60',
                  // Colour is never the sole carrier of state: background,
                  // border and `aria-pressed` move together.
                  pressed
                    ? 'bg-active-wash text-active-wash-ink border-active-wash-ink'
                    : 'bg-surface text-ink border-line hover:bg-hover',
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <p data-testid="send-feedback-attached" className="text-sm text-ink-muted">
        Attached: {attached}
      </p>

      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      {/* Always mounted, so the live region exists before it is filled. */}
      <p role="status" className={sent ? 'text-sm text-ink' : 'sr-only'}>
        {sent ? 'Thanks — sent' : ''}
      </p>

      <DialogFooter>
        <DialogClose asChild>
          <Button type="button" variant="secondary">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit" disabled={!canSend}>
          {isSubmitting ? 'Sending…' : 'Send'}
        </Button>
      </DialogFooter>
    </form>
  );
}
